const { isValid, parse, getDaysInMonth } = require('date-fns')

function safeDateParser (date) {
  if (typeof date !== 'string') {
    return date
  }

  // handle yyyy-mm-dd
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/
  const directParse = parse(date)

  if (isValid(directParse) && isoDateRegex.test(date)) {
    return date
  }

  // handle dd/mm/yyyy or dd-mm-yyyy
  const normalized = date.replace(/[./]/g, '-')
  const parts = normalized.split('-')

  if (parts.length !== 3 || parts.some(p => isNaN(p))) {
    return date
  }

  // pad to handle 1-digit day/month
  let [p1, p2, p3] = parts.map(p => p.padStart(2, '0'))

  if (p3.length !== 4 || p1.length > 2 || p2.length > 2) {
    return date
  }

  const day = parseInt(p1, 10)
  const month = parseInt(p2, 10)
  const year = parseInt(p3, 10)

  if (month < 1 || month > 12) {
    return date
  }

  const daysInMonth = getDaysInMonth(new Date(year, month - 1))

  if (day < 1 || day > daysInMonth[month - 1]) {
    return date
  }

  const isoFormattedDate = `${p3}-${p2}-${p1}`
  const parsed = parse(isoFormattedDate)

  // validate parsed date matches original input components
  if (
    isValid(parsed) &&
    parsed.getDate() === day &&
    parsed.getMonth() + 1 === month &&
    parsed.getFullYear() === year
  ) {
    return isoFormattedDate
  }

  return date
}

function shipmentDateParser (date) {
  if (typeof date !== 'string') {
    return date
  }

  // Always normalize separators first
  // Parses '/' and/or '.' separators to '-' e.g. "31/06/2026" or "31.06.2026" -> "31-06-2026"
  // Parses white spaces e.g. " 31 / 06 / 2026 " -> "31-06-2026"
  const normalizedDate = date.replace(/[./]/g, '-').replace(/\s+/g, '')

  const parts = normalizedDate.split('-')
  if (parts.length !== 3 || parts.some(p => isNaN(+p))) {
    return normalizedDate
  }

  const [p1, p2, p3] = parts

  const dateIsYMD =
    p1.length === 4 &&
    !(p2.length > 2) &&
    !(p3.length > 2)

  const dateIsDMY =
    p3.length === 4 &&
    !(p1.length > 2) &&
    !(p2.length > 2)

  if (dateIsYMD) {
    const day = p3.padStart(2, '0')
    const month = p2.padStart(2, '0')
    return `${day}-${month}-${p1}`
  }

  if (dateIsDMY) {
    const day = p1.padStart(2, '0')
    const month = p2.padStart(2, '0')
    return `${day}-${month}-${p3}`
  }

  return normalizedDate
}

function isValiShipmentDate (date) {
  const [day, month, year] = date.split('-').map(Number)

  if (!day || !month || !year) return false

  const parsedDate = new Date(year, month - 1, day)
  return (
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() + 1 === month &&
    parsedDate.getDate() === day
  )
}

/**
 * Converts dates to Field Supply format (YYYY-MM-DD)
 * This function normalizes dates from various formats (DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD)
 * to the standard FS format (YYYY-MM-DD) required for shipment snapshots.
 *
 * @param {string} date - Date string in various formats
 * @returns {string} Date in YYYY-MM-DD format, or original value if parsing fails
 */
function fsShipmentDateParser (date) {
  if (typeof date !== 'string') {
    return date
  }

  // Normalize separators and whitespace
  const normalizedDate = date.replace(/[./]/g, '-').replace(/\s+/g, '')

  const parts = normalizedDate.split('-')
  if (parts.length !== 3 || parts.some(p => isNaN(+p))) {
    return date
  }

  const [p1, p2, p3] = parts

  // Check if already in YYYY-MM-DD format
  const dateIsYMD =
    p1.length === 4 &&
    !(p2.length > 2) &&
    !(p3.length > 2)

  if (dateIsYMD) {
    const year = p1
    const month = p2.padStart(2, '0')
    const day = p3.padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Check if in DD-MM-YYYY format
  const dateIsDMY =
    p3.length === 4 &&
    !(p1.length > 2) &&
    !(p2.length > 2)

  if (dateIsDMY) {
    const year = p3
    const month = p2.padStart(2, '0')
    const day = p1.padStart(2, '0')
    // Validate the date
    const parsed = parse(`${year}-${month}-${day}`)
    if (isValid(parsed) &&
        parsed.getFullYear() === parseInt(year, 10) &&
        parsed.getMonth() + 1 === parseInt(month, 10) &&
        parsed.getDate() === parseInt(day, 10)) {
      return `${year}-${month}-${day}`
    }
  }

  // If format is unknown, return original
  return date
}

module.exports = {
  safeDateParser,
  shipmentDateParser,
  isValiShipmentDate,
  fsShipmentDateParser
}
